import { Box, Button, styled } from "~/common/mui";
import { C_PRIMARY_DISABLED } from "~/common/components/icon/Colors";

export const Container = styled(Box)({
  display: "flex",
  flexFlow: "column",
});

export const PageTitle = styled("div")(({ theme }) => ({
  ...theme.typography.headlineMedium,
  marginBottom: theme.spacing(2),
  color: theme.palette.text.primary,
}));

export const PageSubTitle = styled("div")(({ theme }) => ({
  ...theme.typography.headlineSmall,
  marginBottom: theme.spacing(2),
  color: theme.palette.text.primary,
}));

export const SelectLabel = styled("div")(({ theme }) => ({
  ...theme.typography.titleMedium,
  fontWeight: "bold",
  color: theme.palette.text.primary,
}));

export const Form = styled(Box)(({ theme }) => ({
  borderRadius: 8,
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  "& .MuiFormControl-root": {
    width: 272,
    [theme.breakpoints.down("desktop-l")]: { width: 228 },
    [theme.breakpoints.down("desktop-m")]: { width: 196 },
    [theme.breakpoints.down("desktop-s")]: { width: 224 },
    [theme.breakpoints.down("tablet")]: { width: 248 },
  },
}));

export const FormRow = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(4),
  [theme.breakpoints.down("desktop-m")]: { gap: theme.spacing(3) },
  [theme.breakpoints.down("desktop-s")]: { flexFlow: "column" },
}));

export const FormColumn = styled("div")({
  display: "flex",
  flexDirection: "column",
});

export const FormRadioMain = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down("tablet")]: { marginBottom: theme.spacing(2) },
}));

export const SectionImageInputs = styled("div")(({ theme }) => ({
  display: "flex",
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down("desktop-s")]: { gap: 100 },
  [theme.breakpoints.down("tablet")]: {
    flexFlow: "column",
    marginBottom: theme.spacing(3),
    gap: 0,
  },
}));

export const DivImageInputs = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  gap: theme.spacing(4),
}));

export const FormItemImage = styled("img")(({ theme }) => ({
  marginRight: theme.spacing(2),
  width: 176,
  height: 104,
  [theme.breakpoints.down("desktop-s")]: {
    marginRight: 0,
    marginBottom: theme.spacing(4),
  },
  [theme.breakpoints.down("tablet")]: { marginBottom: 60 },
}));

export const SectionMobile = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  justifyContent: "space-between",
  flex: 1,
}));

export const Section = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
  "&:last-child": { marginBottom: 0 },
}));

export const FormButtonSection = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  [theme.breakpoints.down("tablet")]: {
    flexFlow: "column-reverse",
    gap: theme.spacing(3),
  },
}));

export const FormItem = styled("div")({ display: "flex" });

export const SpanDisabled = styled("span")({ color: "#c1c0c0" });

export const SpanEnabled = styled("span")(({ theme }) => ({ color: theme.palette.text.primary }));

export const RadioSwitchOptions = styled("div")(({ theme }) => ({
  display: "flex",
  "& > * + *": {
    marginLeft: theme.spacing(4),
  },
}));

export const RadioSwitchOption = styled("div")(({ theme }) => ({
  display: "flex",
  "& .Mui-disabled": {
    color: `${theme.palette.divider} !important`,
  },
  "& .Mui-checked.Mui-disabled": {
    color: `${C_PRIMARY_DISABLED} !important`,
  },
}));

export const ResultTitle = styled("div")(({ theme }) => ({
  ...theme.typography.headlineSmall,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(4),
}));

const resultListVars: Record<string, string | number> = {
  "--grid-column-count": "4",
  "--grid-item--min-width": "350px",
  "--gap-count": "calc(var(--grid-column-count) - 1)",
  "--total-gap-width": "calc(var(--gap-count) * var(--grid-layout-gap))",
  "--grid-item--max-width": "calc((100% - var(--total-gap-width)) / var(--grid-column-count))",
  "--grid-layout-gap": "16px",
};

const resultListVarsTablet: Record<string, string> = { "--grid-layout-gap": "24px" };
const resultListVarsDesktopL: Record<string, string> = { "--grid-layout-gap": "32px" };

export const ResultList = styled("div")(({ theme }) => ({
  ...resultListVars,
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(max(var(--grid-item--min-width), var(--grid-item--max-width)), 1fr))",
  gap: "var(--grid-layout-gap)",
  [theme.breakpoints.up("tablet")]: resultListVarsTablet as Record<string, string>,
  [theme.breakpoints.up("desktop-l")]: resultListVarsDesktopL as Record<string, string>,
}));

export const ResultListItem = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  position: "relative",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 4,
  padding: "42px 16px 16px",
  [theme.breakpoints.up("tablet")]: { padding: "50px 16px 16px" },
  [theme.breakpoints.up("desktop-s")]: { padding: "50px 32px 32px" },
}));

export const ResultListItemBanner = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 8,
  left: 0,
  background: theme.palette.primary.main,
  padding: theme.spacing(1),
  ...theme.typography.titleMedium,
  color: "#fff",
}));

export const ResultListItemRow = styled("div")({ display: "inline-flex" });

export const ResultListItemImage = styled("img")({
  marginRight: 32,
  width: 120,
  height: 120,
  objectFit: "contain",
});

export const ResultListItemContent = styled("div")({
  display: "flex",
  flexFlow: "column",
});

export const ResultListItemName = styled("div")(({ theme }) => ({
  ...theme.typography.titleMedium,
  fontWeight: "bold",
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(2),
}));

export const ResultListItemDescription = styled("div")(({ theme }) => ({
  ...theme.typography.labelLarge,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(2),
}));

export const ResultListItemLink = styled("span")(({ theme }) => ({
  ...theme.typography.labelLarge,
}));

export const EmptyStateSection = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

export const EmptyStateTitle = styled("div")(({ theme }) => ({
  ...theme.typography.headlineMedium,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down("tablet")]: { ...theme.typography.headlineSmall },
}));

export const EmptyStateImage = styled("img")(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

export const EmptyStateDescription = styled("div")(({ theme }) => ({
  ...theme.typography.titleMedium,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(2),
}));

export const EmptyStateList = styled("ol")(({ theme }) => ({
  marginBottom: theme.spacing(4),
  "& li": {
    ...theme.typography.titleMedium,
    color: theme.palette.text.primary,
  },
}));

export const BannerMsg = styled("div")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  background: theme.palette.surface.main,
  border: `1px solid ${theme.palette.divider}`,
  marginTop: theme.spacing(4),
  [theme.breakpoints.down("tablet")]: {
    display: "flex",
    flexFlow: "column",
  },
}));

export const BannerMsgImg = styled("img")(({ theme }) => ({
  objectFit: "cover",
  width: 448,
  height: 80,
  [theme.breakpoints.down("desktop-s")]: {
    width: 224,
    height: 136,
    marginRight: theme.spacing(4),
  },
  [theme.breakpoints.down("tablet")]: {
    width: 360,
    maxWidth: "100%",
    height: 96,
    marginRight: 0,
    marginBottom: theme.spacing(2),
  },
}));

export const BannerMsgText = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  flex: 1,
  marginLeft: theme.spacing(4),
  [theme.breakpoints.down("desktop-s")]: { marginLeft: 0 },
}));

export const BannerMsgTitle = styled("div")(({ theme }) => ({
  ...theme.typography.titleLarge,
  fontWeight: "bold",
  marginBottom: 4,
  [theme.breakpoints.down("tablet")]: {
    marginBottom: 20,
    padding: "0 16px",
  },
}));

export const BannerMsgDescription = styled("div")(({ theme }) => ({
  ...theme.typography.labelLarge,
  color: theme.palette.text.primary,
  [theme.breakpoints.down("desktop-s")]: { flex: 1 },
  [theme.breakpoints.down("tablet")]: {
    marginBottom: 20,
    padding: "0 16px",
  },
}));

export const BannerMsgDescPlusButton = styled("div")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  width: "calc(100% - 16px)",
  [theme.breakpoints.down("tablet")]: {
    flexDirection: "column",
    width: "100%",
  },
}));

export const BannerButton = styled(Button)(({ theme }) => ({
  margin: "0 32px",
  [theme.breakpoints.down("desktop-s")]: { margin: 0 },
  [theme.breakpoints.down("tablet")]: {
    margin: "0 16px 16px 16px",
    width: "calc(100% - 32px)",
  },
}));
